import React from 'react';
import styled, { css } from 'styled-components';
import { color, layout, space } from 'styled-system';
import { colors, IconNotificationSuccess } from '@snsw/react-component-library';

const Separator = styled.div`
  width: 100%;
  margin-top: auto;
  margin-bottom: auto;
  border-top: 1px solid #dee3e5;
`;

const StepTileContainer = styled.div`
  position: relative;
  padding: 1.5rem 1.5rem 0rem 3.5rem;
  :last-child::after {
    content: '';
    display: block;
    width: calc(100% + 1.5rem);
    height: 100%;
    position: relative;
    bottom: -25px;
  }
  &:first-of-type {
    .top {
      border-left: none;
    }
  }
  :last-child .bottom {
    &:before {
      content: '';
      position: absolute;
      width: 14px;
      height: 2px;
      bottom: 0;
      left: 10px;
    }
  }
  ${Separator} {
    margin-top: 0;
    margin-bottom: 1rem;
  }
`;

type CompleteProp = {
  complete: boolean;
};

const Progress = styled.div<CompleteProp>`
  position: absolute;
  width: 20px;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  padding-left: 1rem;
  /* stylelint-disable-next-line */
  .greenTop {
    width: 100%;
    height: 38px;
    min-height: 38px;
    margin-top: 2px;
    border-left: dashed 2px ${colors.green};
  }
  .top {
    width: 100%;
    height: 38px;
    min-height: 38px;
    margin-top: 2px;
    border-left: dashed 2px ${props => (props.complete && colors.white) || colors.green};
  }
  /* stylelint-disable-next-line */
  .bottom {
    width: 100%;
    height: 100%;
    margin-bottom: 2px;
    border-left: dashed 2px ${props => (props.complete && colors.green) || colors.green};
  }
`;

const StepHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  user-select: none;
  outline: none;
  h5 {
    margin-right: 1.4375rem;
  }
  :before {
    content: '';
    display: block;
    width: calc(100% - 3.5rem);
    position: absolute;
    top: 0;
    border-left: dashed 2px ${colors.green};
  }
`;

const StepSubTitle = styled.p`
  width: 100%;
  margin: 0 !important;
`;

type StepLabelContainerProps = {
  success: boolean;
};

const StepLabelContainer = styled.div<StepLabelContainerProps>`
  ${color};
  ${layout};
  ${props =>
        props.success ?
            css`
          border: 1px solid ${colors.green};
          color: ${colors.green};
          background-color: ${colors.white};
        ` :
            `
      border: 2px dashed ${colors.green};
      color: ${colors.green};
      background-color: ${colors.white};
    `};
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  left: 0;
  height: 32px;
  width: 32px;
  border-radius: 1rem;
  text-align: center;
  font-weight: 700;
  margin-right: 0.5rem;
`;

/*
  provide a step's title and text
  or you can just shove your own jsx in instead
  your choice
*/
export type Steps = {
  complete: boolean;
  title?: string;
  text?: any;
  element?: JSX.Element;
};

export type StepperProps = {
  steps: Array<Steps>;
};

export const Stepper: React.FunctionComponent<StepperProps> = ({ steps }: StepperProps) => {
    let previousComplete = false;
    return (
        <>
            {steps.map((step, i) => {
                let topClassName = 'top';
                if (previousComplete) {
                    topClassName = 'greenTop';
                }
                previousComplete = step.complete;
                let bottomClassName = 'bottom';
                const stepsLen = steps.length;
                if (stepsLen - 1 === i) {
                    bottomClassName = 'greenBottom';
                }
                return (
                    <StepTileContainer key={ `Step-${i}` }>
                        <Progress complete={ step.complete }>
                            <div className={ topClassName } />
                            <div className={ bottomClassName } />
                        </Progress>
                        <StepHeader>
                            <StepLabelContainer success={ step.complete }>
                                {step.complete ? <IconNotificationSuccess size='lg' /> : null }
                            </StepLabelContainer>
                            <StepSubTitle>
                                {step.element ? (
                                    step.element
                                ) : (
                                    <>
                                        <strong>{step.title}</strong>
                                        <br />
                                        <span>{step.text}</span>
                                    </>
                                )}
                            </StepSubTitle>
                        </StepHeader>
                    </StepTileContainer>
                );
            })}
        </>
    );
};

export const styledStepper = styled(Stepper)`
  ${space};
`;

// eslint-disable-next-line import/no-anonymous-default-export
export default {
    Stepper: Stepper,
    styledStepper: styledStepper
};
